import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Helper from '@/lib/helper';
import type { StaticListings } from '@/types/StaticListings';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { toast } from 'sonner';

interface _PriceDetails {
    selectedForSaleOrRent: number | null;
    pricingTypes?: StaticListings[];
    selectedPricingType: number | null;
    setSelectedPricingType: React.Dispatch<React.SetStateAction<number | null>>;
    price: number | null;
    setPrice: React.Dispatch<React.SetStateAction<number | null>>;
    maintenanceFee: number | null;
    setMaintenanceFee: React.Dispatch<React.SetStateAction<number | null>>;
    builtUp: string;
    landArea: string;
    markAsDuplicate?: boolean;
    edit?: boolean;
    forTender: boolean;
}

export interface PriceDetailsRef {
    callback: () => boolean;
}

const PriceDetails = forwardRef<PriceDetailsRef, _PriceDetails>(({ 
    selectedForSaleOrRent,
    pricingTypes,
    selectedPricingType,
    setSelectedPricingType,
    price,
    setPrice,
    maintenanceFee,
    setMaintenanceFee,
    builtUp,
    landArea,
    forTender,
    markAsDuplicate,
    edit
}, ref) => {
        const threshold = selectedForSaleOrRent === 1 ? 10000 : 200;
        const [validationErrors, setValidationErrors] = useState<string[]>([]);
        const [pricePerSqft, setPricePerSqft] = useState<number>(0);

        const callback = () => {
            try {
                let valErrors = [];
                if(!forTender) {
                    if(!price) {
                        valErrors.push('price');
                    } else {
                        if(price < threshold) {
                            valErrors.push('price-threshold');
                        }
                    }
                    setValidationErrors(valErrors);
                    if(valErrors.length > 0) throw new Error('Please complete all required fields.');
                }
                return true;
            } catch (err: any) {
                const error = err.response ? err.response.data.error : err.message;
                toast.error(error);
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                return false;
            }
        }

        const removeError = (field: string) => {
            setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
        };

        const calculatePricePerSqft = () => {
            const area = Number(builtUp) || Number(landArea);
            if (!price || !area || area === 0) return 0;
            return price / area;
        };

        useEffect(() => {
            const calculated = calculatePricePerSqft();
            setPricePerSqft(calculated);
        }, [price, builtUp]);

        useImperativeHandle(ref, () => ({
            callback
        }));

        return (
            <section>
                <div className="mt-3 pt-5">
                    <Label className="text-black">Price Type</Label>
                    <div>
                        {
                            pricingTypes?.map((type) => {
                                return (
                                    <Button
                                    type='button'
                                    key={type.value}
                                    variant={selectedPricingType === type.value ? 'default' : 'outline'}
                                    className={`mx-1 mt-3 cursor-pointer ${selectedPricingType !== type.value && 'text-black'}`}
                                    onClick={(e) => {
                                        setSelectedPricingType(type.value);
                                    }}>
                                        {type.label}
                                    </Button>
                                )
                            })
                        }
                    </div>
                </div>
                <div className="mt-3 pt-5">
                    <LabelWithAsterisk label={`${ selectedForSaleOrRent === 1 ? 'Selling' : selectedForSaleOrRent === 2 && 'Rental'} Price`}/>
                    <div className='relative mt-3'>
                        <div className="absolute inset-y-0 left-2 flex items-center">
                            <Badge variant="secondary">
                                RM
                            </Badge>
                        </div>
                        <Input 
                        className={`pl-12 text-xs ${(validationErrors.includes('price') || validationErrors.includes('price-threshold')) && 'border-red-400'}`} 
                        placeholder={`${selectedForSaleOrRent === 1 ? 'Enter selling price' : 'Enter rental price'}`}
                        onKeyDown={Helper.allowOnlyNumbers}
                        value={Helper.formatWithDecimal(Number(price))}
                        onFocus={Helper.handleFocusToEnd}
                        onClick={Helper.handleFocusToEnd}
                        onChange={(e) => {
                            const value = Helper.handleCurrencyChange(e);
                            const numericValue = Number(value);
                            setPrice(isNaN(numericValue) ? 0 : numericValue);
                            if (numericValue >= threshold) {
                                removeError('price-threshold');
                            }
                            if (value) {
                                removeError('price');
                            }
                        }}
                        />
                    </div>
                    {
                        (validationErrors.includes('price') || validationErrors.includes('price-threshold')) && (
                            <span className='text-xs text-red-400'>{validationErrors.includes('price') ? 'Price is required.' : validationErrors.includes('price-threshold') && `Minimum threshold of ${threshold} is required.`}</span>
                        )
                    }
                </div>
                <div className="mt-3 pt-5">
                    <Label className="text-black">Maintenance Fee</Label>
                    <div className='relative mt-3'>
                        <div className="absolute inset-y-0 left-2 flex items-center">
                            <Badge variant="secondary">
                                RM
                            </Badge>
                        </div>
                        <Input 
                        className='pl-12 text-xs' 
                        placeholder='Enter maintenance fee'
                        onKeyDown={Helper.allowOnlyNumbers}
                        value={Helper.formatWithDecimal(Number(maintenanceFee))}
                        onFocus={Helper.handleFocusToEnd}
                        onClick={Helper.handleFocusToEnd}
                        onChange={(e) => {
                            const value = Helper.handleCurrencyChange(e);
                            const numericValue = Number(value);
                            setMaintenanceFee(isNaN(numericValue) ? 0 : numericValue);
                        }}
                        />
                    </div>
                </div>
                {
                    selectedForSaleOrRent === 1 && (
                        <div className="mt-3 pt-5">
                            <Label className="text-black">Price per sqft</Label>
                            <div className='relative mt-3'>
                                <div className="absolute inset-y-0 left-2 flex items-center">
                                    <Badge variant="secondary">
                                        RM
                                    </Badge>
                                </div>
                                <Input 
                                className='pl-12 text-xs' 
                                disabled={true} 
                                value={Helper.formatWithDecimal(pricePerSqft)}/>
                            </div>
                        </div>
                    )
                }
            </section>
        );
    }
);

export default PriceDetails